import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { addBPLog, getBPLogs } from '../../services/firestoreService';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const BP_CATEGORIES = [
  { label: 'Normal', systolic: [0, 120], diastolic: [0, 80], color: '#00e5a0' },
  { label: 'Elevated', systolic: [120, 130], diastolic: [0, 80], color: '#ffb830' },
  { label: 'High Stage 1', systolic: [130, 140], diastolic: [80, 90], color: '#ff8c42' },
  { label: 'High Stage 2', systolic: [140, 999], diastolic: [90, 999], color: '#ff4d6d' },
];

const getBPCategory = (sys, dia) => {
  if (sys < 120 && dia < 80) return BP_CATEGORIES[0];
  if (sys < 130 && dia < 80) return BP_CATEGORIES[1];
  if (sys < 140 || dia < 90) return BP_CATEGORIES[2];
  return BP_CATEGORIES[3];
};

export default function HealthTracker() {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [bpLogs, setBpLogs] = useState([]);
  const [form, setForm] = useState({ systolic: '', diastolic: '', pulse: '', note: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) loadLogs(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLogs = async () => {
    const data = await getBPLogs(user.uid, 30);
    setBpLogs(data.reverse());
  };

  const handleLog = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await addBPLog(user.uid, {
        systolic: parseInt(form.systolic), diastolic: parseInt(form.diastolic),
        pulse: form.pulse ? parseInt(form.pulse) : null, note: form.note,
        date: format(new Date(), 'yyyy-MM-dd HH:mm'),
      });
      toast.success('Blood pressure logged!');
      setForm({ systolic: '', diastolic: '', pulse: '', note: '' });
      loadLogs();
    } catch { toast.error('Failed to log'); }
    finally { setLoading(false); }
  };

  const latest = bpLogs[bpLogs.length - 1];
  const latestCat = latest ? getBPCategory(latest.systolic, latest.diastolic) : null;
  const chartData = bpLogs.map(l => ({ date: l.date?.split(' ')[0] || '', systolic: l.systolic, diastolic: l.diastolic, pulse: l.pulse }));

  return (
    <div style={{ padding: '2rem', maxWidth: '1000px' }} className="animate-fade">
      <h1 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.8rem', fontWeight: 700, color: theme.text, marginBottom: '2rem' }}>❤️ Health Vitals</h1>

      {latest && (
        <div className="card" style={{ marginBottom: '2rem', borderColor: latestCat?.color }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <div style={{ color: theme.textSecondary, fontSize: '0.75rem', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Latest Reading</div>
              <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '2.5rem', fontWeight: 700, color: latestCat?.color }}>
                {latest.systolic}/{latest.diastolic}<span style={{ fontSize: '1rem', color: theme.textSecondary, marginLeft: '0.5rem' }}>mmHg</span>
              </div>
              {latest.pulse && <div style={{ color: theme.textSecondary }}>❤️ {latest.pulse} bpm</div>}
            </div>
            <div style={{ background: `${latestCat?.color}22`, border: `1px solid ${latestCat?.color}`, borderRadius: '12px', padding: '0.75rem 1.25rem', color: latestCat?.color, fontWeight: 600, fontFamily: "'Rajdhani', sans-serif", fontSize: '1.1rem' }}>
              {latestCat?.label}
            </div>
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: '2rem' }}>
        <h3 style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, color: theme.text, marginBottom: '1.25rem' }}>Log Blood Pressure</h3>
        <form onSubmit={handleLog}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '1rem' }}>
            <div className="form-group"><label>Systolic</label><input type="number" value={form.systolic} onChange={e => setForm({...form, systolic: e.target.value})} placeholder="120" required /></div>
            <div className="form-group"><label>Diastolic</label><input type="number" value={form.diastolic} onChange={e => setForm({...form, diastolic: e.target.value})} placeholder="80" required /></div>
            <div className="form-group"><label>Pulse (opt)</label><input type="number" value={form.pulse} onChange={e => setForm({...form, pulse: e.target.value})} placeholder="72" /></div>
          </div>
          <div className="form-group"><label>Note</label><input value={form.note} onChange={e => setForm({...form, note: e.target.value})} placeholder="Before/after medication..." /></div>
          <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : '+ Log Reading'}</button>
        </form>
      </div>

      <div className="card" style={{ marginBottom: '2rem' }}>
        <h3 style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, color: theme.text, marginBottom: '1rem' }}>BP Reference</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
          {BP_CATEGORIES.map(cat => (
            <div key={cat.label} style={{ padding: '0.75rem', borderRadius: '8px', border: `1px solid ${cat.color}44`, background: `${cat.color}11` }}>
              <div style={{ color: cat.color, fontWeight: 600 }}>{cat.label}</div>
              <div style={{ color: theme.textSecondary, fontSize: '0.8rem', marginTop: '0.25rem' }}>
                {cat.systolic[1] === 999 ? `≥${cat.systolic[0]}` : `${cat.systolic[0]}-${cat.systolic[1]}`}/{cat.diastolic[1] === 999 ? `≥${cat.diastolic[0]}` : `${cat.diastolic[0]}-${cat.diastolic[1]}`} mmHg
              </div>
            </div>
          ))}
        </div>
      </div>

      {chartData.length > 1 && (
        <div className="card">
          <h3 style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, color: theme.text, marginBottom: '1rem' }}>BP History Chart</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
              <XAxis dataKey="date" tick={{ fill: theme.textSecondary, fontSize: 11 }} />
              <YAxis domain={[50, 180]} tick={{ fill: theme.textSecondary, fontSize: 11 }} />
              <Tooltip contentStyle={{ background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: '8px', color: theme.text }} />
              <ReferenceLine y={120} stroke="#00e5a0" strokeDasharray="4 4" />
              <Line type="monotone" dataKey="systolic" stroke={theme.accent} strokeWidth={2} dot={{ r: 3 }} name="Systolic" />
              <Line type="monotone" dataKey="diastolic" stroke={theme.success} strokeWidth={2} dot={{ r: 3 }} name="Diastolic" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
